import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Close as CloseIcon, EllipsisVertical as EllipsisVerticalIcon } from "@air/next-icons";
import { DropdownMenu, renderDropdownItems } from "@air/primitive-dropdown-menu";
import { IconButton } from "@air/primitive-icon-button";
import { useBreakpointsContext } from "@air/provider-media-query";
import { animated, config, useSpring } from "@react-spring/web";
import pluralize from "pluralize";
import React, { useMemo, useState } from "react";
import { SELECTION_ACTION_BAR_ID } from "~/constants/selectionActionBar";
import { SELECTION_ACTION_BAR, SELECTION_ACTION_BAR_CLEAR_BTN, SELECTION_ACTION_BAR_ITEMS_SELECTED } from "~/constants/testIDs";
import { SelectionActionBarIconButton } from "./SelectionActionBarIconButton";
const emptyArray = [];
const iconMenuRenderer = (item)=>{
    if (item.type === "item" || item.type === "sub") {
        return /*#__PURE__*/ _jsx(SelectionActionBarIconButton, {
            ...item
        }, item.id);
    }
    return null;
};
/**
 * Displays as a fixed and centered panel on the bottom of the screen
 * Will show the first 4 menu options and move the remaining into an ellipsis menu
 */ export const SelectionActionBarComponent = (param)=>{
    let { menuOptions, numberOfSelectedItems, onClearAll, itemLabel = "item", baseMenuItemRenderer = iconMenuRenderer, quickActionsIds = emptyArray } = param;
    const [actionsContainerRef, setActionsContainerRef] = useState(null);
    const { isAboveSmallScreen } = useBreakpointsContext();
    const actionsContainerWidth = (actionsContainerRef === null || actionsContainerRef === void 0 ? void 0 : actionsContainerRef.clientWidth) || 160;
    const itemPluralText = pluralize(itemLabel, numberOfSelectedItems);
    const itemCountText = !isAboveSmallScreen ? " selected" : " ".concat(itemPluralText, " selected");
    const { dropdownMenuItems, baseMenuItems } = useMemo(()=>{
        const maxIconsAllowed = !isAboveSmallScreen ? 0 : 5;
        const maxIconCountForWidth = actionsContainerWidth / 40;
        const maxIconsCount = Math.min(maxIconCountForWidth, maxIconsAllowed);
        const quickActionsToShow = quickActionsIds.slice(0, maxIconsCount);
        const baseIconIndex = Math.max(0, maxIconsCount - quickActionsToShow.length);
        const optionsWithoutQuickActions = menuOptions.filter((item)=>!quickActionsToShow.includes(item.id));
        const baseMenuItems = menuOptions.filter((item)=>quickActionsToShow.includes(item.id));
        const additionalBaseMenuItems = optionsWithoutQuickActions.filter((item)=>item.type !== "separator").slice(0, baseIconIndex);
        baseMenuItems.push(...additionalBaseMenuItems);
        const dropdownMenuItems = optionsWithoutQuickActions.slice(baseIconIndex);
        return {
            baseMenuItems,
            dropdownMenuItems
        };
    }, [
        actionsContainerWidth,
        isAboveSmallScreen,
        menuOptions,
        quickActionsIds
    ]);
    const [springs] = useSpring(()=>({
            from: {
                opacity: 0,
                y: 16
            },
            to: {
                opacity: 1,
                y: 0
            },
            config: config.stiff
        }));
    return /*#__PURE__*/ _jsxs(animated.li, {
        className: "pointer-events-auto flex h-14 w-full max-w-full items-center rounded-none bg-pigeon-900 px-3 shadow-popover sm:h-16 sm:w-[calc(100%-16px)] sm:max-w-[720px] sm:rounded sm:pl-8 sm:pr-6",
        style: springs,
        "data-testid": SELECTION_ACTION_BAR,
        id: SELECTION_ACTION_BAR_ID,
        children: [
            /*#__PURE__*/ _jsx(IconButton, {
                appearance: "ghost",
                className: "mr-3 text-pigeon-50 hover:bg-pigeon-700 active:bg-pigeon-800",
                color: "grey",
                "data-testid": SELECTION_ACTION_BAR_CLEAR_BTN,
                icon: CloseIcon,
                label: "Clear all",
                onClick: onClearAll,
                size: "medium"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex items-center",
                "data-testid": SELECTION_ACTION_BAR_ITEMS_SELECTED,
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "mr-2 text-14 text-white",
                    children: [
                        /*#__PURE__*/ _jsx("b", {
                            children: numberOfSelectedItems
                        }),
                        itemCountText
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex grow items-center justify-end gap-3",
                ref: (newRef)=>setActionsContainerRef(newRef),
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex gap-3",
                        children: baseMenuItems.map((item)=>item && baseMenuItemRenderer(item))
                    }),
                    !!dropdownMenuItems.length && /*#__PURE__*/ _jsx(DropdownMenu, {
                        sideOffset: 16,
                        trigger: /*#__PURE__*/ _jsx(IconButton, {
                            appearance: "ghost",
                            className: "text-pigeon-50 hover:bg-pigeon-700 active:bg-pigeon-800",
                            color: "grey",
                            "data-testid": "SELECTION_ACTION_BAR_MORE_ACTIONS",
                            label: "More actions",
                            onClick: (event)=>event.stopPropagation(),
                            size: "large",
                            children: /*#__PURE__*/ _jsx(EllipsisVerticalIcon, {
                                className: "size-4"
                            })
                        }),
                        children: renderDropdownItems({
                            options: dropdownMenuItems
                        })
                    })
                ]
            })
        ]
    });
};
